/**
 * RetentionCohortWidget - Ретеншен по когортам
 * ===============================================
 *
 * Когортная таблица удержания: одна строка на дату регистрации,
 * D1/D3/D7/D14/D30 — % когорты, ещё активной на N-й день после регистрации.
 * Отдельная таблица на каждую платформу (стопкой), сортировка общая.
 * Незрелые когорты (ещё не дожившие до дня N) показывают «—».
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { RetentionGeoFilter } from './RetentionGeoFilter';
import { useRetentionGeo } from '../../hooks/useRetentionGeo';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { getPlatformIcon } from '../ui/brand-icons';
import { getPlatformLabel, sortPlatforms } from '../../config/platforms';
import { retention as retentionSpecs } from '@ggstat/chart-specs';

// Колонки D1…D30 — shared/chart-specs/specs/retention.json (их же рисует MCP-сервер)
const COHORT_DAYS = retentionSpecs.cohort.days;

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
}

function SortableHead({ label, sortKey, sortConfig, onSort, className = '' }) {
  return (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {label} {sortConfig.key === sortKey && (sortConfig.direction === 'asc' ? '↑' : '↓')}
    </TableHead>
  );
}

function sortRows(rows, sortConfig) {
  return [...rows].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'regDate') {
      aValue = aValue || '';
      bValue = bValue || '';
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // null (незрелая когорта) — всегда в конце, вне зависимости от направления сортировки
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
}

function CohortTable({ rows, sortConfig, onSort }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead label="Дата когорты" sortKey="regDate" sortConfig={sortConfig} onSort={onSort} />
            <SortableHead label="Размер когорты" sortKey="cohortSize" sortConfig={sortConfig} onSort={onSort} className="text-right" />
            {COHORT_DAYS.map((day) => (
              <SortableHead key={day.key} label={day.label} sortKey={day.key} sortConfig={sortConfig} onSort={onSort} className="text-right" />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.regDate}>
              <TableCell className="font-medium">{formatDate(row.regDate)}</TableCell>
              <TableCell className="text-right">{row.cohortSize.toLocaleString('ru-RU')}</TableCell>
              {COHORT_DAYS.map((day) => (
                <TableCell key={day.key} className="text-right">{formatPercent(row[day.key])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function RetentionCohortWidget() {
  const [sortConfig, setSortConfig] = useState({ key: 'regDate', direction: 'desc' });

  const { geos, geoKey, toggleGeo, clearGeos } = useRetentionGeo();

  const widgetData = useWidgetData(
    async (filters, options) => {
      return MetricsService.getRetentionCohort({ ...filters, geo: geos }, options);
    },
    [geoKey]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data: rows } = widgetProps;

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Одна таблица на платформу: группируем строки, порядок платформ канонический
  const sections = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const byPlatform = new Map();

    list.forEach((row) => {
      const platform = row.platform || 'unknown';
      const bucket = byPlatform.get(platform) || [];
      bucket.push(row);
      byPlatform.set(platform, bucket);
    });

    return sortPlatforms([...byPlatform.keys()]).map((platform) => ({
      platform,
      rows: sortRows(byPlatform.get(platform), sortConfig)
    }));
  }, [rows, sortConfig]);

  return (
    <BaseWidget
      {...widgetProps}
      title="Ретеншен по когортам"
      subtitle="Доля игроков когорты (по дате регистрации), ещё активных на D1/D3/D7/D14/D30 — по платформам"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={6}
      headerAction={
        <RetentionGeoFilter geos={geos} onToggleGeo={toggleGeo} onClearGeos={clearGeos} />
      }
    >
      <div className="space-y-6">
        {sections.map((section) => (
          <div key={section.platform} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              {getPlatformIcon(section.platform, 16)}
              <span>{getPlatformLabel(section.platform)}</span>
              <span className="text-muted-foreground font-normal">
                {section.rows.length} когорт
              </span>
            </div>
            <CohortTable rows={section.rows} sortConfig={sortConfig} onSort={handleSort} />
          </div>
        ))}
      </div>
    </BaseWidget>
  );
}

export default RetentionCohortWidget;
